import { AbstractControl, FormGroup } from '@angular/forms';

export const LOT_MIGRATOR_FORM_ERRORS = {
    duplicateLotNumber: 'duplicateLotNumber',
    required: 'required',
    maxlength: 'maxlength',
    min: 'min',
    nonNegativeWholeNumber: 'nonNegativeWholeNumber',
    integer: 'integer',
    number: 'number',
    postcode: 'postcode'
} as const;

export const LOT_MIGRATOR_FORM_ERROR_MESSAGES = {
    duplicateLotNumber: 'Lot Number already exists in this release stage or another form.',
    required: (label: string) => `${label} is required.`,
    maxlength: (label: string, requiredLength: number) =>
        `${label} must be ${requiredLength} characters or fewer.`,
    min: (label: string) => `${label} must be 0 or greater.`,
    integer: (label: string) => `${label} must be a whole number.`,
    number: (label: string) => `${label} must be a number.`,
    postcode: (label: string) => `${label} must be exactly 4 digits.`,
    nonNegativeWholeNumber: (label: string) =>
        `${label} must be a whole number 0 or greater.`,
    invalid: (label: string) => `${label} is invalid.`
} as const;

const ERROR_PRIORITY: string[] = [
    LOT_MIGRATOR_FORM_ERRORS.duplicateLotNumber,
    LOT_MIGRATOR_FORM_ERRORS.required,
    LOT_MIGRATOR_FORM_ERRORS.maxlength,
    LOT_MIGRATOR_FORM_ERRORS.min,
    LOT_MIGRATOR_FORM_ERRORS.nonNegativeWholeNumber,
    LOT_MIGRATOR_FORM_ERRORS.integer,
    LOT_MIGRATOR_FORM_ERRORS.number,
    LOT_MIGRATOR_FORM_ERRORS.postcode
];

export const hasLotMigratorFormError = (
    lotForm: FormGroup,
    controlName: string
): boolean => {
    const control = lotForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
};

export const getLotMigratorFormErrorMessage = (
    lotForm: FormGroup,
    controlName: string,
    label: string
): string => {
    const control = lotForm.get(controlName);
    if (!control) {
        return '';
    }

    return resolveControlErrorMessage(control, label);
};

const resolveControlErrorMessage = (
    control: AbstractControl,
    label: string
): string => {
    for (const errorKey of ERROR_PRIORITY) {
        if (!control.hasError(errorKey)) {
            continue;
        }

        switch (errorKey) {
            case LOT_MIGRATOR_FORM_ERRORS.duplicateLotNumber:
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.duplicateLotNumber;
            case LOT_MIGRATOR_FORM_ERRORS.required:
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.required(label);
            case LOT_MIGRATOR_FORM_ERRORS.maxlength: {
                const requiredLength = control.getError(LOT_MIGRATOR_FORM_ERRORS.maxlength)
                    ?.requiredLength;
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.maxlength(label, requiredLength);
            }
            case LOT_MIGRATOR_FORM_ERRORS.min:
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.min(label);
            case LOT_MIGRATOR_FORM_ERRORS.nonNegativeWholeNumber:
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.nonNegativeWholeNumber(label);
            case LOT_MIGRATOR_FORM_ERRORS.integer:
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.integer(label);
            case LOT_MIGRATOR_FORM_ERRORS.number:
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.number(label);
            case LOT_MIGRATOR_FORM_ERRORS.postcode:
                return LOT_MIGRATOR_FORM_ERROR_MESSAGES.postcode(label);
            default:
                break;
        }
    }

    return LOT_MIGRATOR_FORM_ERROR_MESSAGES.invalid(label);
};
